import type { MessageCode } from '~/constants/MessageCode';
import { MESSAGE_FALLBACK } from '~/constants/MessageFallback';

/**
 * メッセージに関するロジック
 */
export const useMessage = () => {
  const { showAlert, showConfirm, showError } = useCommonModalStore();
  const masterStore = useMasterStore();

  /**
   * 画面メッセージをモーダル表示する
   *
   * @param displayId 画面ID
   * @param messageCode メッセージコード
   * @param isConfirm 確認形式のモーダルかどうか
   * @param onOk OKボタン押下時の処理
   * @param onCancel キャンセルボタン押下時の処理
   * @param params メッセージパラメータ（$1, $2などを置換する値）
   * @param acceptButtonText OKボタンの表示名（確認モーダルの場合のみ使用）
   * @param cancelButtonText キャンセルボタンの表示名（確認モーダルの場合のみ使用）
   */
  const showClientMessage = (
    displayId: string,
    messageCode: MessageCode,
    isConfirm?: boolean,
    onOk?: () => void,
    onCancel?: () => void,
    params?: string[],
    acceptButtonText?: string,
    cancelButtonText?: string,
  ) => {
    // ストアからメッセージ情報を取得する
    const messageInfo = _getMessageInfo(messageCode, params, displayId);
    if (!messageInfo) {
      return;
    }

    // isConfirmが指定された場合は確認モーダル
    if (isConfirm === true) {
      showConfirm(
        messageInfo.messageContent,
        messageInfo.messageType === '2',
        onOk,
        onCancel,
        acceptButtonText,
        cancelButtonText,
      );
    }
    // メッセージ区分が「1: error」の場合はエラーモーダル
    else if (messageInfo.messageType === '1') {
      showError(
        displayId,
        messageInfo.messageCd,
        messageInfo.messageContent,
        onOk,
      );
    }
    // 上記以外の場合はアラートモーダル
    else {
      showAlert(
        messageInfo.messageContent,
        messageInfo.messageType,
        onOk,
      );
    }
  };

  /**
   * APIレスポンスのメッセージを表示する
   *
   * @param displayId 画面ID
   * @param status ステータス(メッセージコード)
   * @param message メッセージ本文
   * @param onClose OKボタン押下時の処理
   */
  const showApiMessage = (
    displayId: string,
    status: string,
    message: string,
    onOk?: () => void,
  ) => {
    // APIメッセージは固定でエラー表示とする
    showError(
      displayId,
      status,
      _removeNewlineEscape(message),
      onOk,
    );
  };

  /**
   * メッセージコード指定で直接メッセージを取得する
   *
   * @param messageCode メッセージコード
   * @param params メッセージパラメータ（$1, $2などを置換する値）
   * @returns メッセージコードに紐づくメッセージ
   */
  const getMessage = (messageCode: MessageCode, params?: string[]) => {
    const messageInfo = _getMessageInfo(messageCode, params);
    return messageInfo ? messageInfo.messageContent : '';
  };

  /**
   * メッセージ情報を取得する
   *
   * @param messageCode メッセージコード
   * @param params 埋め込みパラメータ
   * @returns メッセージ情報
   */
  const _getMessageInfo = (messageCode: MessageCode, params?: string[], displayId?: string) => {
    const logger = useLogger(displayId ?? '0-00');

    // ストアからメッセージ情報を取得する
    let messageInfo = masterStore.getMessage(messageCode);

    // メッセージが取得できない場合はフォールバックメッセージを使用する
    if (!messageInfo) {
      logger.warn('メッセージの取得に失敗しました。フォールバックメッセージを使用します。', undefined, {
        messageCode,
      });
      const fallbackMessage = MESSAGE_FALLBACK[messageCode] || null;

      if (!fallbackMessage) {
        logger.error('フォールバックメッセージが見つかりませんでした。', undefined, {
          messageCode,
        });
        return null;
      }

      messageInfo = fallbackMessage;
    }

    // メッセージの改行コードのエスケープを除去する
    messageInfo.messageContent = _removeNewlineEscape(messageInfo.messageContent);

    // パラメータが指定されている場合は埋め込みを行う
    if (params && params.length > 0) {
      let messageContent = messageInfo.messageContent;
      params.forEach((param, index) => {
        messageContent = messageContent.replace(`$${index + 1}`, param);
      });
      return {
        ...messageInfo,
        messageContent,
      };
    }

    return messageInfo;
  };

  /**
   * メッセージ内の改行コードエスケープを除去する
   *
   * @param messageContent メッセージ内容
   * @returns 改行コードエスケープを除去したメッセージ内容
   */
  const _removeNewlineEscape = (messageContent: string) => {
    return messageContent?.replace(/\\n/g, '\n') ?? '';
  };

  return {
    showClientMessage,
    showApiMessage,
    getMessage,
  };
};
